import {
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useWorkspaceKeys, type WorkspaceKeys } from "../../app/current-user.tsx";
import { researchApi } from "../../client.ts";
import type {
  Advice,
  AdviceSummary,
  CreateAdviceInput,
  UpdateAdviceInput,
} from "../../domain/schema.ts";
import { useWorkspaceMutation } from "../../lib/workspace-mutation.ts";
import { workspaceQuery, type WorkspaceQuery } from "../../lib/workspace-query.ts";

export const adviceListQuery = (keys: WorkspaceKeys): WorkspaceQuery<AdviceSummary[]> =>
  workspaceQuery(keys.adviceList(), () => researchApi.listAdvice());

export const adviceQuery = (keys: WorkspaceKeys, id: string): WorkspaceQuery<Advice> =>
  workspaceQuery(keys.advice(id), () => researchApi.getAdvice(id));

export const useAdviceList = (): UseQueryResult<AdviceSummary[]> =>
  useQuery(adviceListQuery(useWorkspaceKeys()));

export const useAdvice = (id: string): UseQueryResult<Advice> =>
  useQuery(adviceQuery(useWorkspaceKeys(), id));

/** Fetches the advice again (also when cached), e.g. to reset the editor after a 409 STALE. */
export const useReloadAdvice = (id: string): (() => Promise<Advice>) => {
  const queryClient = useQueryClient();
  const keys = useWorkspaceKeys();
  return () => queryClient.query({ ...adviceQuery(keys, id), staleTime: 0 });
};

export const useCreateAdvice = (): UseMutationResult<Advice, Error, CreateAdviceInput> =>
  useWorkspaceMutation({
    mutationFn: (input: CreateAdviceInput) => researchApi.admin.createAdvice(input),
    cache: (keys, advice) => ({ set: [[keys.advice(advice.id), advice]] }),
  });

export const useUpdateAdvice = (id: string): UseMutationResult<Advice, Error, UpdateAdviceInput> =>
  useWorkspaceMutation({
    mutationFn: (input: UpdateAdviceInput) => researchApi.admin.updateAdvice(id, input),
    cache: (keys, advice) => ({ set: [[keys.advice(advice.id), advice]] }),
  });

export const useDeleteAdvice = (): UseMutationResult<void, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.deleteAdvice(id),
    cache: (keys, _data, id) => ({ remove: [keys.advice(id)] }),
  });
